import type { HTMLAttributes } from 'react';

import { faSquare } from '@fortawesome/pro-regular-svg-icons';
import { faSquareCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Field, Label, Switch } from '@headlessui/react';
import { clsx } from 'clsx';

export interface CheckboxProps {
    checked?: boolean;
    defaultChecked?: boolean;
    onChange?: (value: boolean) => void;
    disabled?: boolean;
    label: string;
    dataTestid?: string;
    name: string;
    description?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
}

export const Checkbox = ({
    checked,
    defaultChecked,
    onChange,
    disabled = false,
    label,
    dataTestid,
    name,
    description,
    htmlAttributes,
}: CheckboxProps) => (
    <div
        className={clsx('group/checkbox flex items-center', {
            'text-gray-400 cursor-not-allowed': disabled,
            'text-gray-800 hover:cursor-pointer': !disabled,
        })}
        data-testid={dataTestid}
        role='checkbox'
        data-component-name='Checkbox'
        {...htmlAttributes}
    >
        <Field className='flex items-start'>
            <Switch
                checked={checked}
                onChange={(value) => {
                    if (!disabled && onChange) {
                        onChange(value);
                    }
                }}
                defaultChecked={defaultChecked}
                name={name}
            >
                <FontAwesomeIcon
                    icon={checked ? faSquareCheck : faSquare}
                    className={clsx(['p-1', 'rounded-sm', 'flex'], {
                        'text-gray-500': !checked && !disabled,
                        'group-hover/checkbox:bg-gray-200': !disabled,
                        'text-gray-400 cursor-not-allowed': disabled,
                    })}
                />
            </Switch>
            {label && (
                <Label
                    className={clsx('pl-2 text-sm', {
                        'cursor-not-allowed': disabled,
                        'hover:cursor-pointer': !disabled,
                    })}
                >
                    {label}
                    {description && <p className='mt-2 text-xs text-gray-600'>{description}</p>}
                </Label>
            )}
        </Field>
    </div>
);
